import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Shield,
  FileText,
  Clock,
  DollarSign,
  Settings,
  Users,
  Activity,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { mockClientsService as clientsService } from '@/services/api/mock-clients-data';
import { cn } from '@/lib/utils';
import type { ClientRiskData, RiskLevel, RiskCategory } from '@/types/client';

const riskLevelConfig: Record<RiskLevel, { label: string; className: string; bgClassName: string }> = {
  critical: {
    label: 'Critical',
    className: 'text-red-600 border-red-500/30',
    bgClassName: 'bg-red-500/10 hover:bg-red-500/20',
  },
  high: {
    label: 'High',
    className: 'text-orange-600 border-orange-500/30',
    bgClassName: 'bg-orange-500/10 hover:bg-orange-500/20',
  },
  medium: {
    label: 'Medium',
    className: 'text-amber-600 border-amber-500/30',
    bgClassName: 'bg-amber-500/10 hover:bg-amber-500/20',
  },
  low: {
    label: 'Low',
    className: 'text-blue-600 border-blue-500/30',
    bgClassName: 'bg-blue-500/10 hover:bg-blue-500/20',
  },
  none: {
    label: 'No Risk',
    className: 'text-green-600 border-green-500/30',
    bgClassName: 'bg-green-500/10 hover:bg-green-500/20',
  },
};

const riskCategoryConfig: Record<RiskCategory, { label: string; icon: React.ReactNode }> = {
  compliance: { label: 'Compliance', icon: <Shield className="h-4 w-4" /> },
  documentation: { label: 'Documentation', icon: <FileText className="h-4 w-4" /> },
  expiry: { label: 'Expiry', icon: <Clock className="h-4 w-4" /> },
  financial: { label: 'Financial', icon: <DollarSign className="h-4 w-4" /> },
  operational: { label: 'Operational', icon: <Settings className="h-4 w-4" /> },
};

export function RiskHeatmapPage() {
  const navigate = useNavigate();
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<RiskCategory | 'all'>('all');

  // Fetch risk data
  const { data: riskData, isLoading } = useQuery({
    queryKey: ['risk-heatmap', riskFilter, categoryFilter],
    queryFn: () => clientsService.getRiskHeatmap({
      riskLevel: riskFilter !== 'all' ? (riskFilter as RiskLevel) : undefined,
      category: categoryFilter !== 'all' ? categoryFilter : undefined,
    }),
  });

  const clients = riskData?.clients || [];
  const stats = riskData?.stats;

  const getRiskBadge = (risk: RiskLevel) => {
    const config = riskLevelConfig[risk];
    return (
      <Badge variant="outline" className={cn('text-xs font-medium', config.className)}>
        {config.label}
      </Badge>
    );
  };

  const getCategoryRiskIndicator = (risk: RiskLevel) => {
    const colors = {
      critical: 'bg-red-500',
      high: 'bg-orange-500',
      medium: 'bg-amber-500',
      low: 'bg-blue-500',
      none: 'bg-green-500',
    };
    return <div className={cn('h-2 w-2 rounded-full', colors[risk])} />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Practice-Wide Risk Heatmap</h1>
          <p className="text-muted-foreground">
            Visual overview of risk distribution across all clients
          </p>
        </div>
        <Button variant="outline">
          <Activity className="mr-2 h-4 w-4" />
          Refresh Analysis
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.totalClients}</p>
                <p className="text-xs text-muted-foreground mt-1">Total Clients</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{stats.criticalRisk}</p>
                <p className="text-xs text-muted-foreground mt-1">Critical</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">{stats.highRisk}</p>
                <p className="text-xs text-muted-foreground mt-1">High</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.mediumRisk}</p>
                <p className="text-xs text-muted-foreground mt-1">Medium</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.lowRisk}</p>
                <p className="text-xs text-muted-foreground mt-1">Low</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{stats.noRisk}</p>
                <p className="text-xs text-muted-foreground mt-1">No Risk</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.averageHealthScore}</p>
                <p className="text-xs text-muted-foreground mt-1">Avg Score</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Select value={riskFilter} onValueChange={setRiskFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by risk level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Risk Levels</SelectItem>
                  <SelectItem value="critical">Critical Only</SelectItem>
                  <SelectItem value="high">High Only</SelectItem>
                  <SelectItem value="medium">Medium Only</SelectItem>
                  <SelectItem value="low">Low Only</SelectItem>
                  <SelectItem value="none">No Risk Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as RiskCategory | 'all')}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                  <SelectItem value="documentation">Documentation</SelectItem>
                  <SelectItem value="expiry">Expiry</SelectItem>
                  <SelectItem value="financial">Financial</SelectItem>
                  <SelectItem value="operational">Operational</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Risk Heatmap Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Client Risk Matrix</CardTitle>
          <CardDescription>
            {clients.length} client{clients.length !== 1 ? 's' : ''} displayed
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-medium">No clients match the selected filters</p>
              <p className="text-sm text-muted-foreground mt-1">
                Try adjusting your filters
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {clients.map((client: ClientRiskData) => (
                <div
                  key={client.clientId}
                  className={cn(
                    'p-4 rounded-lg border transition-colors cursor-pointer',
                    riskLevelConfig[client.overallRisk].bgClassName
                  )}
                  onClick={() => navigate(`/panel/files/clients/${client.clientId}`)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{client.clientName}</h3>
                        <Badge variant="outline" className="text-xs">
                          {client.clientType}
                        </Badge>
                        {getRiskBadge(client.overallRisk)}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Activity className="h-3 w-3" />
                          Health Score: {client.healthScore}
                        </span>
                        {client.criticalIssues > 0 && (
                          <span className="flex items-center gap-1 text-red-600">
                            <AlertTriangle className="h-3 w-3" />
                            {client.criticalIssues} critical
                          </span>
                        )}
                        {client.highIssues > 0 && (
                          <span className="flex items-center gap-1 text-orange-600">
                            <TrendingDown className="h-3 w-3" />
                            {client.highIssues} high
                          </span>
                        )}
                        {client.mediumIssues > 0 && (
                          <span className="flex items-center gap-1 text-amber-600">
                            <TrendingUp className="h-3 w-3" />
                            {client.mediumIssues} medium
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Risk Breakdown by Category */}
                  <div className="grid grid-cols-5 gap-3 text-xs">
                    {Object.entries(riskCategoryConfig).map(([category, config]) => (
                      <div
                        key={category}
                        className="flex items-center gap-2 p-2 rounded bg-background/50"
                      >
                        <div className="flex items-center gap-1.5 flex-1">
                          {config.icon}
                          <span className="text-muted-foreground truncate">{config.label}</span>
                        </div>
                        {getCategoryRiskIndicator(client.riskBreakdown[category as RiskCategory])}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
